"use client";

import { H } from "@ecco/ui/a11y-heading";
import { Box } from "@ecco/ui/box";
import { Flex } from "@ecco/ui/flex";
import { NextImage } from "@ecco/ui/next-image";
import dynamic from "next/dynamic";
import { useEffect, useRef, useState, type FC } from "react";
import { HeroCallToActions } from "../hero-content/HeroCallToActions";
import type { HeroProps } from "../Hero.types";
import { useHeroModule } from "../useHeroModule";
import { outsideProps } from "./HeroOutsideStyle";
import { RichText } from "../../rich-text-renderer/RichText";
import HeroVideoControls from "../hero-content/HeroVideoControls";
import { useHeroVideoMute, useHeroVideoTabOrder, getHeroMediaToggleProps } from "../heroVideo";
import { CommonCMS } from "@lib/constants/contentful";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";

const HeroVideoPlayer = dynamic(
    () => import("../HeroVideoPlayer").then((mod) => mod.HeroVideoPlayer),
    { ssr: false }
);

function getCtaButtonProps(type: "outline" | "solid" | undefined) {
    if (type === "outline") {
        return { variant: "outline" as const, colorScheme: "black" as const };
    }

    return { colorScheme: "black" as const };
}

export const HeroOutside: FC<HeroProps> = (props) => {
    const {
        id,
        title,
        description,
        legalText,
        callToActions,
        loop = false,
        isCarousel = false,
    } = props;
    const hasCallToActions = Boolean(callToActions?.length);
    const titleRef = useRef<HTMLElement>(null);
    const [isTitleMultiline, setIsTitleMultiline] = useState(false);
    const {
        promotionImage,
        shouldRenderVideo,
        shouldRenderImage,
        hasDesktopImage,
        hasMobileImage,
        desktopImageUrl,
        desktopImageAlt,
        mobileImageUrl,
        mobileImageAlt,
        shouldRenderContent,
        shouldShowControls,
        currentVideoId,
        isVideoMuted,
        setIsVideoMuted,
        videoPlayerMuted,
        shouldPlayVideo,
        isPlaybackConfirmed,
        isPosterVisible,
        isAboveTheFold,
        videoRef,
        promotionRef,
        setVideoPlaying,
        toggleVideoPlayback,
        onPlayerPlayingChange,
        onPlayerEnded,
        shouldMountVideo,
        posterUrl,
    } = useHeroModule({
        ...props,
        respectReducedMotion: true,
        desktopBreakpoint: "sm",
    });

    const videoPlayerId = `hero-video-${id}`;
    const { toggleMute } = useHeroVideoMute(isVideoMuted, setIsVideoMuted, videoPlayerId);
    const { getMultiple } = useMicrocopy();
    const videoAria = getMultiple(CommonCMS.shared, [
        CommonCMS.video.ariaPlay,
        CommonCMS.video.ariaPause,
    ]);

    useHeroVideoTabOrder(videoPlayerId, shouldMountVideo && Boolean(currentVideoId));

    useEffect(() => {
        const titleElement = titleRef.current;
        if (!titleElement) {
            setIsTitleMultiline(false);
            return;
        }

        const updateTitleLines = () => {
            const lineHeight = Number.parseFloat(window.getComputedStyle(titleElement).lineHeight);
            setIsTitleMultiline(titleElement.getBoundingClientRect().height > lineHeight + 1);
        };

        updateTitleLines();
        const observer = new ResizeObserver(updateTitleLines);
        observer.observe(titleElement);

        return () => observer.disconnect();
    }, [title]);

    return (
        <Flex
            ref={promotionRef}
            data-testid="Hero"
            {...outsideProps.root}
            data-video-ready={isPlaybackConfirmed}
            width="full"
        >
            {(shouldRenderVideo || shouldRenderImage) && (
                <Box {...outsideProps.mediaOuter}>
                    <Box
                        {...outsideProps.mediaInner}
                        ref={shouldRenderVideo ? videoRef : undefined}
                        data-title-multiline={isTitleMultiline ? "true" : undefined}
                        data-poster-visible={
                            shouldRenderVideo && isPosterVisible && posterUrl ? true : undefined
                        }
                        {...getHeroMediaToggleProps({
                            enabled: Boolean(shouldMountVideo && currentVideoId),
                            isPlaying: isPlaybackConfirmed,
                            playLabel: videoAria[CommonCMS.video.ariaPlay],
                            pauseLabel: videoAria[CommonCMS.video.ariaPause],
                            onToggle: toggleVideoPlayback,
                        })}
                    >
                        {shouldMountVideo && currentVideoId && (
                            <Box {...outsideProps.mediaFill}>
                                <HeroVideoPlayer
                                    videoId={currentVideoId}
                                    isPlaying={shouldPlayVideo}
                                    isMuted={videoPlayerMuted}
                                    loop={loop}
                                    style={{
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                        width: "100%",
                                        height: "100%",
                                    }}
                                    id={videoPlayerId}
                                    onPlayingChange={onPlayerPlayingChange}
                                    onEnded={onPlayerEnded}
                                />
                            </Box>
                        )}
                        {shouldRenderImage && (
                            <>
                                {hasMobileImage && (
                                    <NextImage
                                        fill
                                        objectFit="cover"
                                        priority={isAboveTheFold}
                                        alt={mobileImageAlt ?? desktopImageAlt ?? title}
                                        src={mobileImageUrl}
                                        {...outsideProps.mobileImage}
                                        loading={isAboveTheFold ? "eager" : "lazy"}
                                        sizes={["350px", "736px", "960px", "1302px"]}
                                    />
                                )}
                                {hasDesktopImage && (
                                    <NextImage
                                        fill
                                        priority={isAboveTheFold}
                                        src={desktopImageUrl}
                                        alt={desktopImageAlt ?? title}
                                        loading={isAboveTheFold ? "eager" : "lazy"}
                                        {...(hasMobileImage
                                            ? outsideProps.desktopImageWithMobile
                                            : {})}
                                        sizes={["350px", "736px", "960px", "1302px"]}
                                        objectFit="cover"
                                    />
                                )}
                            </>
                        )}
                        {!shouldRenderImage && posterUrl && (
                            <NextImage
                                loading={isAboveTheFold ? "eager" : "lazy"}
                                priority={isAboveTheFold}
                                alt={desktopImageAlt ?? title}
                                src={posterUrl}
                                sizes={["350px", "736px", "960px", "1302px"]}
                                fill
                                objectFit="cover"
                                aria-hidden={!isPosterVisible}
                                css={{
                                    ...outsideProps.videoPoster.css,
                                    opacity: isPosterVisible ? 1 : 0,
                                    transition: "opacity 300ms ease",
                                    pointerEvents: "none",
                                }}
                            />
                        )}
                    </Box>

                    {shouldShowControls && shouldMountVideo && (
                        <HeroVideoControls
                            appearance="outside"
                            isVideoMuted={isVideoMuted}
                            toggleVideoMute={toggleMute}
                            isVideoPlaying={isPlaybackConfirmed}
                            setVideoPlaying={setVideoPlaying}
                            isCarousel={isCarousel}
                            videoPlayerId={videoPlayerId}
                        />
                    )}
                </Box>
            )}

            {shouldRenderContent && (
                <Box {...outsideProps.content}>
                    <Flex {...outsideProps.contentRow}>
                        <Flex {...outsideProps.textColumn}>
                            {title && (
                                <H
                                    aria-label={title}
                                    {...outsideProps.title}
                                    ref={titleRef}
                                    level={isAboveTheFold ? "h1" : null}
                                >
                                    {title}
                                </H>
                            )}
                            {description && <Box {...outsideProps.description}>{description}</Box>}
                        </Flex>
                        {(legalText || hasCallToActions) && (
                            <Flex
                                {...outsideProps.ctaColumn}
                                data-has-legal-text={legalText ? "true" : undefined}
                            >
                                {legalText && (
                                    <Box {...outsideProps.legalText}>
                                        <RichText data={legalText} />
                                    </Box>
                                )}
                                {hasCallToActions && (
                                    <Flex {...outsideProps.ctaRow}>
                                        <HeroCallToActions
                                            title={title}
                                            id={id}
                                            callToActions={callToActions}
                                            promotionImage={promotionImage}
                                            slideIndex={props.slideIndex ?? 0}
                                            buttonStyle={outsideProps.button}
                                            iconProps={{ size: "sm" }}
                                            getButtonProps={(button) =>
                                                getCtaButtonProps(button.variant)
                                            }
                                        />
                                    </Flex>
                                )}
                            </Flex>
                        )}
                    </Flex>
                </Box>
            )}
        </Flex>
    );
};
